import { Injectable } from '@angular/core';
import { ReservationComponent } from '@internal-components/event-components/reservation-component/reservation.component';
import { AttendanceStatus } from '@internal-models/attendance.model';
import { EventAttendanceType, EventInterval, EventLocationType, IEvent } from '@internal-models/event.model';
import { IQuestionCategory } from '@internal-models/question-category.model';
import { IReservation } from '@internal-models/reservation.model';
import { AttendanceProvider } from '@internal-providers/attendance.provider';
import { EventProvider } from '@internal-providers/event.provider';
import { QuestionCategoryProvider } from '@internal-providers/question-category.provider';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import { Action, ErrorType, NavigationDataKey, NavigationType, UserPermissions } from '@shared-libs/enums';
import { IFormGroup } from '@shared-libs/interfaces';
import { ICalendarEvent } from '@shared-models/calendar-event.model';
import { IUserDetails } from '@shared-models/user.model';
import { NavigationService } from '@shared-services/navigation.service';
import { isAfter, isToday } from 'date-fns';
import { Observable } from 'rxjs';
import { translate } from '@shared-libs/helpers';
import { WaitListDetailsComponent } from '@internal-components/wait-list-components/wait-list-component/wait-list-details.component';
import { IWaitListItem } from '@internal-models/wait-list-item.model';
import { ClientProvider } from '@internal-providers/client.provider';
import { ErrorService } from '@shared-services/error.service';
import { MessageService } from '@shared-services/message.service';
import { IWalkInAttendance } from '@internal-models/walk-in-attendance.model';
import { WalkInAttendanceFormFactory } from '@internal-factories/forms/walk-in-attendance-form.factory';
import { ValidationService } from '@shared-services/validation.service';

@Injectable({ providedIn: 'root' })
export class EventDetailsController {
	public event: IEvent;
	public calendarEvent: ICalendarEvent;
	public isInPast: boolean;
	public hasStarted: boolean;
	public mapIsReady: boolean;
	public isToday: boolean;
	public attendancesVisible: boolean;
	public userDetails: Observable<IUserDetails>;
	public questionCategories: Array<IQuestionCategory>;

	public walkInHeadcount: number;
	public walkInAttendance: IWalkInAttendance;
	public walkInAttendanceForm: IFormGroup<IWalkInAttendance>;

	public readonly EventInterval: typeof EventInterval = EventInterval;
	public readonly EventAttendanceType: typeof EventAttendanceType = EventAttendanceType;
	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly AttendanceStatus: typeof AttendanceStatus = AttendanceStatus;
	public readonly EventLocationType: typeof EventLocationType = EventLocationType;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		public readonly navigationService: NavigationService,
		private readonly eventProvider: EventProvider,
		public readonly attendanceProvider: AttendanceProvider,
		private readonly questionCategoryProvider: QuestionCategoryProvider,
		private readonly clientProvider: ClientProvider,
		private readonly errorService: ErrorService,
		private readonly messageService: MessageService,
		private readonly validationService: ValidationService
	) {}

	public async onInit(): Promise<void> {
		this.event = this.navigationService.getNavigationData(NavigationDataKey.event);
		this.calendarEvent = this.navigationService.getNavigationData(NavigationDataKey.calendarEvent);
		this.isInPast = !isAfter(new Date(this.calendarEvent.endsAt), new Date());
		this.hasStarted = !isAfter(new Date(this.calendarEvent.startsAt), new Date());
		this.isToday = isToday(new Date(this.calendarEvent.startsAt));
		this.event = await this.eventProvider.getOneForDay(this.event.id, this.calendarEvent.id);
		this.attendancesVisible = this.isToday || this.isInPast;
		await this.getQuestionCategories();
		this.initializeForm();
	}

	public onDestroy(): void {
		this.walkInAttendanceForm = null;
	}

	public async initializeForm(): Promise<void> {
		const attendance = await this.attendanceProvider.getAttendanceForWalkIn(this.event.id, this.calendarEvent.id);
		this.walkInAttendanceForm = WalkInAttendanceFormFactory.create(this.questionCategories, attendance);
	}

	private async getQuestionCategories(): Promise<void> {
		this.questionCategories = await this.questionCategoryProvider.getAll();
	}

	public close(): void {
		this.navigationService.navigateBack();
	}

	public async changeAttendanceStatus(reservation: IReservation): Promise<void> {
		if (reservation.Attendance.status !== AttendanceStatus.Present) {
			reservation.Attendance.status = AttendanceStatus.Present;
			await this.attendanceProvider.updateStatus(reservation.Attendance.id, { status: AttendanceStatus.Present });
		} else {
			reservation.Attendance.status = AttendanceStatus.Absent;
			await this.attendanceProvider.updateStatus(reservation.Attendance.id, { status: AttendanceStatus.Absent });
		}
		this.messageService.showSavedMessage(translate('MOBILE_VIEW.CHANGES_SAVED'));
	}

	public async openReservation(reservation?: IReservation): Promise<void> {
		if (
			this.eventIsFullyBooked() &&
			!reservation &&
			(this.event.attendanceType !== EventAttendanceType.Registration || !this.attendancesVisible)
		) {
			return this.errorService.showApplicationError(translate('EVENT_DETAILS.OVERBOOK'), ErrorType.Component);
		} else if (!reservation && !(await this.confirmOverbookingWhenAllowed())) {
			return;
		}

		void this.navigationService.navigateTo(NavigationType.modal, ReservationComponent, {
			reservation,
			calendarEvent: this.calendarEvent,
			eventAttendanceType: this.event.attendanceType,
		});
	}

	public async openWaitList(): Promise<void> {
		void this.navigationService
			.navigateTo<{ action: Action }>(NavigationType.modal, WaitListDetailsComponent, {
				action: Action.CREATE,
				event: this.event,
			})
			.then((result) => {
				if (result?.action) {
					this.messageService.showSavedMessage();
				}
			})
			.catch();
	}

	public async openReservationFromWaitList(waitListItem?: IWaitListItem): Promise<void> {
		if (
			this.eventIsFullyBooked() &&
			(this.event.attendanceType !== EventAttendanceType.Registration || !this.attendancesVisible)
		) {
			return this.errorService.showApplicationError(translate('EVENT_DETAILS.OVERBOOK'), ErrorType.Component);
		}

		if (!(await this.confirmOverbookingWhenAllowed())) {
			return;
		}

		void this.navigationService.navigateTo(NavigationType.modal, ReservationComponent, {
			reservation:
				this.event.attendanceType === EventAttendanceType.Reservation
					? this.event.Reservations.filter((reservation) => !reservation.Client)?.[0]
					: null,
			calendarEvent: this.calendarEvent,
			eventAttendanceType: this.event.attendanceType,
			waitListItem: { ...waitListItem, Client: await this.clientProvider.getOne(waitListItem.Client.id) },
		});
	}

	public async openWalkInReservation(reservation?: IReservation): Promise<void> {
		void this.navigationService.navigateTo(NavigationType.modal, ReservationComponent, {
			reservation,
			calendarEvent: this.calendarEvent,
			eventAttendanceType: this.event.attendanceType,
		});
	}

	public async saveAttendances() {
		await this.attendanceProvider.saveAttendanceForWalkIn(
			this.event.id,
			this.calendarEvent.id,
			this.walkInAttendanceForm.value
		);
		this.messageService.showSavedMessage();
	}

	public async confirmOverbookingWhenAllowed(): Promise<boolean> {
		if (this.event.attendanceType !== EventAttendanceType.Registration) {
			return true;
		}
		if (!this.eventIsFullyBooked()) {
			return true;
		}
		if (!this.attendancesVisible) {
			return false;
		}

		return this.validationService.actionIsConfirmed('OVERBOOK');
	}

	public eventIsFullyBooked(): boolean {
		if (this.event.attendanceType === EventAttendanceType.Reservation) {
			return this.event.Reservations?.filter((reservation) => !reservation.Client).length === 0;
		}

		return (
			this.event.Reservations?.filter((reservation) => reservation.isActive).length >= this.event.maxRegistrations
		);
	}
}
